import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { PageHero, Reveal, SectionHead } from "../components/common/Section";
import ConsultationForm from "../components/forms/ConsultationForm";
import { SERVICE_ICONS, APPROACH } from "../data/content";
import { api } from "../lib/api";
import NotFound from "./NotFound";
import CourseGrid from "../components/learning/CourseGrid";

const FORM_SERVICE = {
  "insurance-advisory": "Insurance Advisory",
  "mutual-fund-solutions": "Mutual Fund Advisory",
  "investment-planning": "Investment Solutions",
  "wealth-management": "Wealth Management & Tax Planning",
  "financial-education": "Financial Education",
};

function Items({ items, Icon }) {
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {items.map((it, i) => (
        <motion.div key={it.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
          whileHover={{ y: -4 }} data-testid={`service-item-${i}`} className="rounded-2xl border border-slate-200 bg-white p-7 transition-shadow hover:shadow-xl">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-mint/10 text-mint-600"><Icon size={18} /></div>
          <h3 className="mt-5 text-lg font-bold text-navy">{it.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.description}</p>
        </motion.div>
      ))}
    </div>
  );
}

export default function ServicePage({ slug }) {
  const [svc, setSvc] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setSvc(null);
    api.get(`/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!svc) return <div className="min-h-[60vh] bg-navy" data-testid="service-loading" />;
  const Icon = SERVICE_ICONS[svc.icon] || CheckCircle2;

  return (
    <>
      <Seo title={svc.title} description={svc.summary} path={`/${slug}`} schema={[
        { "@context": "https://schema.org", "@type": "Service", name: svc.title, description: svc.summary, provider: { "@type": "FinancialService", name: "Quantora" }, areaServed: "IN" },
        breadcrumbSchema([["Home", "/"], ["Services", "/services"], [svc.title, `/${slug}`]]),
      ]} />
      <PageHero eyebrow={svc.activity_type} title={svc.title} text={svc.summary}>
        <p className="mt-4 text-lg font-medium text-gold">{svc.tagline}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a href="#consult" data-testid="service-book-btn" className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3.5 text-sm font-semibold text-navy hover:-translate-y-0.5 transition-transform">Book Free Consultation <ArrowRight size={16} /></a>
          <Link to="/calculators" data-testid="service-calculators-link" className="inline-flex rounded-full border border-white/20 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/5">Try our calculators</Link>
        </div>
      </PageHero>
      <section className="bg-slate-100/70 py-24">
        <div className="container-q">
          <SectionHead eyebrow="What we cover" title={`${svc.title} at Quantora`} className="mb-12" />
          <Items items={svc.items} Icon={Icon} />
        </div>
      </section>
      {slug === "financial-education" && (
        <section className="py-24" data-testid="education-courses-section">
          <div className="container-q">
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
              <SectionHead eyebrow="Free courses" title="Beginner to advanced learning paths" text="Short lessons, key takeaways and progress tracking in your client portal." />
              <Link to="/learn" data-testid="education-all-courses-link" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-mint-600">All courses <ArrowRight size={15} /></Link>
            </div>
            <CourseGrid />
          </div>
        </section>
      )}
      <section className="py-20">
        <div className="container-q grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <Reveal>
            <SectionHead eyebrow="How it works" title="Our process" />
            <ol className="mt-8 space-y-5">
              {APPROACH.map((s) => <li key={s.step} className="flex gap-4"><span className="font-num text-sm text-gold-600">{s.step}</span><div><p className="font-semibold text-navy">{s.title}</p><p className="text-sm text-slate-600">{s.text}</p></div></li>)}
            </ol>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="service-compliance-note" className="rounded-3xl border border-gold/40 bg-gold-50 p-8">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-600"><AlertTriangle size={18} /> Important disclosure</div>
              <p className="mt-4 text-sm leading-relaxed text-slate-700">{svc.compliance_note}</p>
              <Link to="/regulatory-disclosures" className="mt-4 inline-block text-sm font-semibold text-navy underline-offset-4 hover:underline" data-testid="service-regulatory-link">Read regulatory disclosures</Link>
            </div>
          </Reveal>
        </div>
      </section>
      <section id="consult" className="scroll-mt-24 bg-slate-100/70 py-24">
        <div className="container-q">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-xl sm:p-12">
            <h2 className="text-3xl font-bold text-navy">Book your free {svc.title.toLowerCase()} consultation</h2>
            <p className="mb-10 mt-2 text-slate-600">Share a few details and our team will get in touch.</p>
            <ConsultationForm defaultService={FORM_SERVICE[slug]} />
          </div>
        </div>
      </section>
    </>
  );
}
